import { Constants } from '@praktio/common';
import { last } from 'lodash';
import { MessageTypes } from './constants';
import type { LearnerToParentMessage } from '@praktio/common-ui';
import { isEmpty } from './functions';

import { hashId, isExerciseSlide } from './slide';

const { AnnotationTypes, TemplateTypes, QuizStates, CataTypes } = Constants;

const searchParams = new URLSearchParams(window.location.search);

export const isSlideDataIncluded = () => {
  return searchParams?.has('slideIncluded') ? !!searchParams.get('slideIncluded') : false;
};
export const getQueryParamsSlideId = () => {
  return searchParams?.has('slideId') ? searchParams.get('slideId') || undefined : undefined;
};

export const determineQuizData = ({
  slide,
  clearSelected,
}: {
  slide: Slide;
  clearSelected?: boolean;
}) => {
  const {
    type,
    timeline,
    tries: slideTries,
    paragraphSelection,
    isSingle,
    subType,
    isLast,
    _id,
  } = slide;
  const quizData: Quiz = {
    tries: 0,
    completed: false,
    disableNext: isExerciseSlide(type) || isLast,
    showSubmit: isExerciseSlide(type),
    outOfTries: false,
    stateType: undefined,
    showTries: false,
    selectedAnswer: undefined,
  };

  const learnerSlideData = slide.learnerData;
  const { tries: learnerTries, selected, started, correct, completed } = learnerSlideData || {};
  if (learnerSlideData) {
    quizData.tries = learnerTries || 0;
    quizData.completed = completed;
    if (completed && selected) {
      if (type === TemplateTypes.ClickBestSpot && !paragraphSelection) {
        quizData.selected = (selected as string[]).filter((x) => x !== '-1').reverse()[0];
        quizData.selected = hashId(quizData.selected.toString());

        // TODO - Prior setup, multi-select
        // quizData.selected = [(selected as string[]).filter((x) => x !== '-1').reverse()[0]];
        // quizData.selected = [hashId(quizData.selected.toString())];
      }
    } else if (clearSelected) {
      quizData.selected = [];
    }

    if (
      completed ||
      correct ||
      // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
      (!isEmpty(slideTries) && (learnerTries || 0) >= slideTries)
    ) {
      quizData.disableNext = isLast;
      quizData.showSubmit = false;
      quizData.outOfTries = true;
      quizData.stateType = correct ? QuizStates.Correct : QuizStates.AllWrong;
      if (type === TemplateTypes.ClickBestSpot && timeline) {
        slide.timeline.forEach((r) => {
          if (
            paragraphSelection
              ? r.subType === AnnotationTypes.Choice && r.correct
              : r.subType === AnnotationTypes.Correct
          ) {
            r.active = true;
            if (quizData.stateType === QuizStates.Correct) {
              r.className = `${paragraphSelection ? AnnotationTypes.Choice + ' ' : ''}${
                AnnotationTypes.Correct
              }`;
            } else if (quizData.stateType === QuizStates.AllWrong) {
              r.className = `${paragraphSelection ? AnnotationTypes.Choice + ' ' : ''}${
                AnnotationTypes.Incorrect
              }`;
            }
          }
        });

        if (paragraphSelection) {
          const lastSelected = last(selected as string[]);
          if (lastSelected) {
            quizData.selected = lastSelected;
          }
        }
      } else if ([TemplateTypes.CheckAllApply, TemplateTypes.ChooseBestProvision].includes(type)) {
        const lastSelected = last(selected as string[][]);
        if (lastSelected) {
          quizData.selected = lastSelected;
        }

        if (
          type === TemplateTypes.CheckAllApply &&
          quizData.selected &&
          (isSingle || subType === CataTypes.EditHorizontal || subType === CataTypes.EditVertical)
        ) {
          const selected = Array.isArray(quizData.selected)
            ? quizData.selected[0]
            : quizData.selected;
          const choice = timeline.find((a) => a._id === selected);
          if (choice) {
            quizData.selectedAnswer = choice;
          }
        }
      }
    } else {
      // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
      if (!isEmpty(learnerTries) && learnerTries > 0) {
        quizData.showTries = true;
        quizData.stateType = QuizStates.SomeWrong;
      } else if (!learnerTries && learnerTries !== 0) {
        quizData.selected = [];
      }
    }
  } else {
    quizData.selected = [];
  }

  postSlideData({
    tries: learnerTries,
    started,
    correct: !isExerciseSlide(type) || correct,
    completed,
    _id,
  });
  return quizData;
};

export const resetStatus = () => {
  const win = window as any;
  if (win.ResetStatus) {
    win.ResetStatus();
  }
};

export const setCompleted = () => {
  const win = window as any;
  if (win.SetPassed && win.SetReachedEnd) {
    console.log('Completed module; Making Litmos API calls to SetPassed and SetReachedEnd ...');
    win.SetPassed();
    win.SetReachedEnd();
  }

  // TODO: If we plan to make the LMS SCORM compliant, we'll
  // need to adjust this to be similar to above
  if (window.parent && document.referrer) {
    window.parent.postMessage({ type: MessageTypes.Completed }, document.referrer);
  }
};

export const showNextModule = (data: {
  show: boolean;
  advanceNext?: boolean;
  unlocked?: boolean;
  completed?: boolean;
}) => {
  if (window.parent && document.referrer) {
    window.parent.postMessage(
      { type: MessageTypes.NextModule, data } satisfies LearnerToParentMessage,
      document.referrer,
    );
  }
};

export const postSlideData = (data: any) => {
  if (window.parent && document.referrer) {
    window.parent.postMessage(
      { type: MessageTypes.Slide, data } satisfies LearnerToParentMessage,
      document.referrer,
    );
  }
};

export const notifyLoaded = () => {
  if (window.parent && document.referrer) {
    window.parent.postMessage({ type: MessageTypes.Loaded }, document.referrer);
  }
};

export const notifySlideLoaded = (data: any) => {
  if (window.parent && document.referrer) {
    window.parent.postMessage(
      { type: MessageTypes.SlideLoaded, data } satisfies LearnerToParentMessage,
      document.referrer,
    );
  }
};

export const notifySlideLoading = () => {
  if (window.parent && document.referrer) {
    window.parent.postMessage({ type: MessageTypes.SlideLoading }, document.referrer);
  }
};

export const showContactUs = () => {
  if (window.parent && document.referrer) {
    window.parent.postMessage({ type: MessageTypes.ContactUs }, document.referrer);
  }
};

interface Errors extends Error {
  message: string;
  response: {
    status: number;
    data: string;
  };
}

export const notifyError = (e: unknown) => {
  const error = e as Errors;
  let data = error.message;
  if (error.response) {
    if (error.response.data) {
      data = error.response.data;
    }
  }

  console.error(e);
  if (window.parent && document.referrer) {
    window.parent.postMessage(
      { type: MessageTypes.Error, data } satisfies LearnerToParentMessage,
      document.referrer,
    );
  }
};
